import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

import { duplicateNodeAtCursor } from "@/components/input/utils/duplicateNodeAtCursor";
import { createLongPressTracker } from "@/utils/longPress";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

/**
 * Single owner of the long-press gesture: resting a finger on a math node duplicates
 * it at the caret. This is the touch counterpart of the alternate click that
 * `FlatNode` dispatches through `matchClick`.
 *
 * A press completes while the finger is still down, so there is no touch event left to
 * cancel. The compatibility mouse events synthesized after `touchend` are swallowed
 * instead — left alone they reach `FlatNode` and move the caret onto the pressed node,
 * undoing where the duplicate placed it.
 *
 * A press on a node the caret cannot take a copy of is left alone, so it still reads
 * as a plain tap.
 */
export const LongPress = Extension.create<{ doc: DocumentModel }>({
  name: "longPress",

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        view(view) {
          /** The node under the finger, captured before the press completes. */
          let pressed: NodeModel | undefined;
          /** Set once a press has acted, so the mouse events it leaves behind are dropped. */
          let handled = false;

          const tracker = createLongPressTracker(() => {
            if (pressed && duplicateNodeAtCursor(doc, pressed)) {
              handled = true;
            }
          });

          const mathNodeAt = (target: EventTarget | null) => {
            const element = target instanceof HTMLElement ? target : null;
            const nodeId = element?.closest<HTMLElement>("[data-math-node-id]")?.dataset.mathNodeId;
            return nodeId ? doc.nodes.get(nodeId) : undefined;
          };

          const onTouchStart = (e: TouchEvent) => {
            handled = false;

            // A second finger means a pinch or a scroll, never a press.
            if (e.touches.length !== 1) {
              tracker.cancel();
              return;
            }

            pressed = mathNodeAt(e.target);
            if (pressed) tracker.start(e.touches[0]);
          };

          const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 1) {
              tracker.cancel();
              return;
            }

            const touch = e.changedTouches[0];
            if (touch) tracker.move(touch);
          };

          const onTouchEnd = () => tracker.cancel();

          // A cancelled touch sends no compatibility mouse events, so none are left to drop.
          const onTouchCancel = () => {
            tracker.cancel();
            handled = false;
          };

          /**
           * Runs in the capture phase, so the event is stopped before it reaches the
           * React root. `click` closes the synthesized sequence and clears the flag.
           */
          const swallowCompatMouse = (e: Event) => {
            if (!handled) return;
            if (e.type === "click") handled = false;

            e.stopPropagation();
            if (e.cancelable) e.preventDefault();
          };

          // Nothing here cancels a touch, so every touch listener stays passive.
          const { dom } = view;
          dom.addEventListener("touchstart", onTouchStart, { passive: true });
          dom.addEventListener("touchmove", onTouchMove, { passive: true });
          dom.addEventListener("touchend", onTouchEnd, { passive: true });
          dom.addEventListener("touchcancel", onTouchCancel, { passive: true });
          dom.addEventListener("mousedown", swallowCompatMouse, true);
          dom.addEventListener("mouseup", swallowCompatMouse, true);
          dom.addEventListener("click", swallowCompatMouse, true);

          return {
            destroy() {
              tracker.cancel();
              dom.removeEventListener("touchstart", onTouchStart);
              dom.removeEventListener("touchmove", onTouchMove);
              dom.removeEventListener("touchend", onTouchEnd);
              dom.removeEventListener("touchcancel", onTouchCancel);
              dom.removeEventListener("mousedown", swallowCompatMouse, true);
              dom.removeEventListener("mouseup", swallowCompatMouse, true);
              dom.removeEventListener("click", swallowCompatMouse, true);
            },
          };
        },
      }),
    ];
  },
});
